/*
 * Heritage Memo — pending-memo request store.
 *
 * Companion to heritage-reserve.js and heritage-vault.js. A memo
 * request is a whole-cart snapshot submitted to MDJ at one point in
 * time — the partner asked to receive these N pieces on memo
 * (10-day viewing) and the cart was cleared after submission.
 *
 * Local-first: the partner's memo history lives in localStorage on
 * the device. On submit (and on dashboard resubmit) the request is
 * posted to the JewelersDigital intake endpoint via
 * MDJIntegrations.sendMemo (memo_request.v1) — that's the actionable
 * notification to MDJ. This replaced the old Shopify contact-form email,
 * which Shopify silently dropped on its CAPTCHA check. State does not
 * sync across devices today — a future iteration could persist to a
 * backend keyed on customer.id.
 *
 * Storage shape:
 *   localStorage["mdj.memos.items"] = [
 *     {
 *       id: string,                // request UUID
 *       requestedAt: number,       // unix ms
 *       status: 'pending'          // future: 'completed' once MDJ
 *                                  //         confirms ship/return
 *       note: string,
 *       total_price: number,       // cents, cart.total_price at submit
 *       currency: string,
 *       items: [
 *         { id, handle, title, image, era, sku, url, qty, line_price }
 *       ],
 *       contact: { full customer context — same shape as reserves }
 *     },
 *     ...
 *   ]
 *
 * Public API on window.MDJMemos:
 *   getAll(): request[]
 *   get(id): request | undefined
 *   has(id): boolean
 *   add(request, context): Promise<boolean>
 *                                 — stores locally (idempotent on id) then
 *                                   posts to the JD intake. Resolves true
 *                                   when JD accepted it. The local write
 *                                   happens first, so the request is in the
 *                                   dashboard (and resubmittable) even if
 *                                   the post fails.
 *   remove(id, context): void     — removes locally. Cancellation is
 *                                   local-only for now (the JD intake is
 *                                   create-only; no cancel event in v1).
 *   resubmit(id, context): Promise<boolean>
 *                                 — re-posts an already-stored request to
 *                                   the JD intake without re-adding it.
 *                                   Same memo id ⇒ JD dedupes (no double-
 *                                   booking). Resolves false when the
 *                                   endpoint is unconfigured/failed — the
 *                                   caller surfaces the screenshot fallback.
 *   markCompleted(id): void       — moves a request to status 'completed'
 *   subscribe(fn): unsub
 *
 * Custom events (dispatched on document):
 *   mdj:memos-changed { detail: { items, action, item } }
 */

(function () {
  const KEY = 'mdj.memos.items';
  const subscribers = new Set();

  function safeStorage() {
    try {
      const k = '__mdj_memo_test__';
      window.localStorage.setItem(k, k);
      window.localStorage.removeItem(k);
      return window.localStorage;
    } catch (e) {
      return null;
    }
  }

  function read() {
    const s = safeStorage();
    if (!s) return [];
    try {
      const raw = s.getItem(KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function write(items) {
    const s = safeStorage();
    if (!s) return;
    try {
      s.setItem(KEY, JSON.stringify(items));
    } catch (e) {
      if (window.console) console.warn('MDJMemos: localStorage write failed', e);
    }
  }

  function notify(action, item, items) {
    const detail = { action: action, item: item, items: items };
    document.dispatchEvent(new CustomEvent('mdj:memos-changed', { detail: detail }));
    subscribers.forEach((fn) => {
      try { fn(detail); } catch (e) { /* user fn shouldn't break the store */ }
    });
  }

  // Post a stored request to the JewelersDigital intake (memo_request.v1).
  // Returns Promise<boolean>; false when MDJIntegrations/the endpoint
  // isn't available, so callers can fall back without feature-flagging.
  function intakeSend(item, context) {
    if (!window.MDJIntegrations || !window.MDJIntegrations.sendMemo) {
      return Promise.resolve(false);
    }
    return Promise.resolve(window.MDJIntegrations.sendMemo(item, context || {}));
  }

  function newId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return 'memo-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }

  function getAll() {
    return read();
  }

  function get(id) {
    if (id == null) return undefined;
    return read().find((it) => String(it.id) === String(id));
  }

  function has(id) {
    return !!get(id);
  }

  function add(request, context) {
    if (!request || !request.items || request.items.length === 0) return Promise.resolve(false);
    const items = read();
    const id = request.id || newId();
    const stamped = Object.assign({}, request, {
      id: id,
      requestedAt: Date.now(),
      status: 'pending'
    });
    // Idempotent on id: a retried submit (same memo id) replaces the
    // existing entry rather than stacking a duplicate in the dashboard.
    const existingIdx = items.findIndex((it) => String(it.id) === String(id));
    if (existingIdx >= 0) items.splice(existingIdx, 1, stamped);
    else items.unshift(stamped);
    write(items);
    notify('add', stamped, items);
    // Store first (above), then notify MDJ — so the request survives in
    // the dashboard and stays resubmittable even if the post fails.
    return intakeSend(stamped, context);
  }

  function remove(id, context) {
    if (id == null) return;
    const items = read();
    const idx = items.findIndex((it) => String(it.id) === String(id));
    if (idx < 0) return;
    const [removed] = items.splice(idx, 1);
    write(items);
    notify('remove', removed, items);
    // Cancellation is local-only for now: the JD intake is create-only
    // (memo_requests:create) with no cancel event in memo_request.v1.
    // Wire a cancel signal here once JD exposes one.
  }

  /*
   * Re-post a request that's already stored locally to the JD intake.
   * Used by the account dashboard "Resubmit" button to recover a request
   * MDJ never received (e.g. one submitted before the intake was wired,
   * via the contact form Shopify silently dropped).
   *
   * Does NOT mutate the store — it's the same request, sent again. The
   * caller passes the partner's current contact context (store name,
   * email, address on file) so the payload is self-contained even though
   * the original submit-time contact isn't persisted locally. The memo id
   * is unchanged, so JD dedupes a request it already has rather than
   * double-booking.
   *
   * Returns Promise<boolean> so the button can show a real "Resent ✓" /
   * fallback state. Resolves false when the endpoint isn't configured or
   * the post fails — the dashboard then points the partner at the
   * screenshot fallback.
   */
  function resubmit(id, context) {
    const item = get(id);
    if (!item) return Promise.resolve(false);
    return intakeSend(item, context);
  }

  function markCompleted(id) {
    if (id == null) return;
    const items = read();
    const idx = items.findIndex((it) => String(it.id) === String(id));
    if (idx < 0) return;
    items[idx] = Object.assign({}, items[idx], { status: 'completed' });
    write(items);
    notify('mark-completed', items[idx], items);
  }

  function subscribe(fn) {
    if (typeof fn !== 'function') return () => {};
    subscribers.add(fn);
    return () => subscribers.delete(fn);
  }

  window.MDJMemos = { getAll, get, has, add, remove, resubmit, markCompleted, subscribe };
})();
